// Една група на „Излишък от продажби" — един склад: заглавен ред с отметка на
// целия склад (три състояния) и продуктите под него. Редът е бутон-отметка
// (клик / Space / Enter), а менюто „⋯" до него дава разбивката по отчети и
// компенсиране само на този продукт. На тесен екран редът става карта; на
// широк е ЕДИН плътен ред (`ROW_DENSE`): името и приглушено след него
// категорията, посл. цена и отчетите — отрязаното е в подсказката.
//
// Знакът на количеството е ВИНАГИ минус: в данните излишъкът е положителен,
// минусът е само визуален (това е липса, не излишък).
import { useTranslation } from "react-i18next";
import { CheckBox } from "../../components/filters/CheckBox";
import { RowActionsMenu } from "../../components/list/RowActionsMenu";
import { ROW_DENSE, ROW_HEADER, ROW_HEADER_TEXT, ROW_HEIGHT, ROW_INNER } from "../../components/list/density";
import { TruncatedText } from "../../components/list/TruncatedText";
import { formatQty } from "../../components/qty";
import { useInventoryShell } from "../../components/shell/InventoryShellContext";
import {
  formatDate,
  formatMoney,
  formatUnitPrice,
  type ShortageRow,
  type ShortageWarehouseGroup as Group,
} from "./shortageShared";

// Отметка · Продукт · Количество · Нето · Бруто (менюто „⋯" е извън решетката)
const ROW_GRID_CLASS =
  "grid grid-cols-[18px_minmax(0,1fr)_150px_100px_110px] items-center gap-3";

// Бутонът-отметка: antd reset-ът маха outline-а → собствен пръстен при фокус
const TOGGLE_CLASS =
  "cursor-pointer border-none text-left text-ink outline-none focus-visible:shadow-[inset_0_0_0_2px_var(--color-brand)]";

type Props = {
  group: Group;
  selected: Set<string>;
  onToggle: (keys: string[], on: boolean) => void;
  onBreakdown: (row: ShortageRow) => void;
  onCompensateOne: (row: ShortageRow) => void;
};

// „-1,500 кг" в червено хапче
const DeficitPill = ({ row }: { row: ShortageRow }) => (
  <span className="inline-block whitespace-nowrap rounded-full bg-danger-tint px-2.5 pb-[3px] pt-[2px] font-sheet text-[12.5px] font-bold leading-[normal] text-danger-ink">
    -{formatQty(row.quantity, row.decimals)}
    {row.unit ? <span className="font-normal">&nbsp;{row.unit}</span> : null}
  </span>
);

export const ShortageWarehouseGroup = ({
  group,
  selected,
  onToggle,
  onBreakdown,
  onCompensateOne,
}: Props) => {
  const { t } = useTranslation();
  const { isNarrow } = useInventoryShell();
  const keys = group.rows.map((row) => row.key);
  const pickedCount = keys.filter((key) => selected.has(key)).length;
  const allPicked = pickedCount === keys.length;

  // Категория · посл. доставна цена · от кои отчети е натрупан
  const metaOf = (row: ShortageRow) =>
    [
      row.category,
      row.lastDeliveryPrice != null
        ? `${t("inventoryPage.colLastPriceShort")} ${formatUnitPrice(row.lastDeliveryPrice)}`
        : null,
      row.breakdown.length && row.earliestReportDate
        ? t("inventoryPage.shortageReportsInfo", {
            count: row.breakdown.length,
            date: formatDate(row.earliestReportDate),
          })
        : null,
    ]
      .filter(Boolean)
      .join(" · ");

  const menuOf = (row: ShortageRow) => (
    <RowActionsMenu
      compact={isNarrow}
      dense={!isNarrow}
      actions={[
        {
          key: "breakdown",
          label: t("inventoryPage.shortageBreakdownOpen"),
          onClick: () => onBreakdown(row),
        },
        {
          key: "compensate",
          label: t("inventoryPage.compensateBackdated"),
          hint: t("inventoryPage.shortageCompensateOneHint"),
          onClick: () => onCompensateOne(row),
        },
      ]}
    />
  );

  return (
    // shrink-0 — в скролиращата колона не се свива (виж SectionCard)
    <div className="shrink-0 overflow-hidden rounded-xl border border-shade-2 bg-panel">
      {/* Заглавие: отметка на целия склад */}
      <button
        type="button"
        data-shortage-row
        role="checkbox"
        aria-checked={allPicked ? true : pickedCount ? "mixed" : false}
        onClick={() => onToggle(keys, !allPicked)}
        className={`${TOGGLE_CLASS} flex w-full flex-wrap items-center gap-3 bg-fill px-3.5 ${ROW_DENSE}`}
      >
        <CheckBox checked={allPicked} indeterminate={!allPicked && pickedCount > 0} />
        <span className="min-w-0 flex-1 truncate text-[15px] font-semibold leading-5">
          {group.warehouseName}
        </span>
        <span className="text-[13px] text-body">
          {t("inventoryPage.shortageProductsCount", { count: group.rows.length })}
        </span>
        <span className="font-sheet font-semibold">{formatMoney(group.gross)}</span>
      </button>

      {/* Заглавията на колоните — само в широкия изглед */}
      {!isNarrow && (
        <div className={`flex items-center gap-2 border-t border-shade-1 px-3.5 ${ROW_HEADER}`}>
          <div className={`${ROW_GRID_CLASS} flex-1 ${ROW_HEADER_TEXT}`}>
            <span />
            <span>{t("inventoryPage.colProductName")}</span>
            <span className="text-right">{t("inventoryPage.colDeficitQty")}</span>
            <span className="text-right">{t("inventoryPage.colNet")}</span>
            <span className="text-right">{t("inventoryPage.colGross")}</span>
          </div>
          <span className="w-7 shrink-0 pointer-coarse:w-10" />
        </div>
      )}

      {group.rows.map((row) => {
        const on = selected.has(row.key);
        const meta = metaOf(row);
        return (
          <div
            key={row.key}
            className={`flex gap-2 border-t border-shade-1 ${
              isNarrow ? "items-start px-3.5 py-3" : `items-stretch px-3.5 ${ROW_HEIGHT}`
            } ${on ? "bg-brand-tint" : "hover:bg-fill"}`}
          >
            <button
              type="button"
              data-shortage-row
              role="checkbox"
              aria-checked={on}
              onClick={() => onToggle([row.key], !on)}
              className={`${TOGGLE_CLASS} min-w-0 flex-1 rounded-lg bg-transparent ${
                isNarrow ? "flex flex-col gap-1.5 px-0 py-0.5" : `${ROW_GRID_CLASS} ${ROW_INNER} px-0`
              }`}
            >
              {isNarrow ? (
                <>
                  <span className="flex items-center gap-2.5">
                    <CheckBox checked={on} />
                    <span className="min-w-0 flex-1 text-[15px] font-semibold leading-[1.3]">
                      {row.productName}
                    </span>
                  </span>
                  {meta && (
                    <span className="pl-7 text-[12.5px] leading-[1.35] text-body">{meta}</span>
                  )}
                  <span className="flex flex-wrap items-center gap-x-3 gap-y-1 pl-7 text-[12.5px] text-body">
                    <DeficitPill row={row} />
                    <span>
                      {t("inventoryPage.colNet")}{" "}
                      <span className="font-sheet text-ink-2">{formatMoney(row.quantityNet)}</span>
                    </span>
                    <span>
                      {t("inventoryPage.colGross")}{" "}
                      <span className="font-sheet font-bold text-ink">{formatMoney(row.quantityGross)}</span>
                    </span>
                  </span>
                </>
              ) : (
                <>
                  <CheckBox checked={on} />
                  <TruncatedText
                    text={meta ? `${row.productName} · ${meta}` : row.productName}
                    className="text-[13.5px] font-semibold"
                  >
                    {row.productName}
                    {meta && <span className="text-xs font-normal text-body"> · {meta}</span>}
                  </TruncatedText>
                  <span className="text-right">
                    <DeficitPill row={row} />
                  </span>
                  <span className="text-right font-sheet text-[13px] text-ink-2">
                    {formatMoney(row.quantityNet)}
                  </span>
                  <span className="text-right font-sheet text-[13px] font-bold text-ink">
                    {formatMoney(row.quantityGross)}
                  </span>
                </>
              )}
            </button>
            <div className="flex shrink-0 items-center">{menuOf(row)}</div>
          </div>
        );
      })}
    </div>
  );
};

export default ShortageWarehouseGroup;
